import React, { useState, useEffect, useContext } from 'react';
import { Container, Box, Chip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Paper, Snackbar, Alert, Button, TableFooter } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { SharedContext } from './../context';
import { motion } from "framer-motion";
import { crypt } from './../utils/crypt'
import { formatCurrency } from './../utils/validators'
import axios from 'axios';
import CouponCard from "./CouponCard";

// Funções utilitárias para formatação
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('pt-BR', { month: 'short' })
    .replace('.', '')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
  const currentYear = new Date().getFullYear();
  const year = date.getFullYear() < currentYear ? `/${String(date.getFullYear()).slice(-2)}` : '';
  return `${day}/${month}${year}`;
};

const Invoices = () => {
  const [selected, setSelected] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [message, setMessage] = useState('');
  const navigate = useNavigate(); // Hook do React Router para navegação
  const { sharedState, setSharedState } = useContext(SharedContext); // Utilize o contexto
  // const { companyName } = useParams();
  const companyName = process.env.REACT_APP_COMPANY;
  const [isLoading, setIsLoading] = useState(false);
  const [coupons, setCoupons] = useState(sharedState.coupons || []);

  useEffect(() => {
    if (sharedState.paymentStatus && sharedState.paymentStatus === 'created') {
      setSharedState(null);
      localStorage.removeItem('data');
      localStorage.removeItem('cupom');
      navigate(`/login`);
    }
  }, []);

  useEffect(() => {
    const allSelected = sharedState.invoices.length > 0 && 
                        sharedState.invoices.every((_, index) => selected[index]);
    setSelectAll(allSelected);
  }, [selected, sharedState.invoices]);

  const handleCheckboxChange = (index) => {
    const invoice = sharedState.invoices[index];
    const newSelection = { ...selected };

    if (!newSelection[index]) {
      newSelection[index] = true;

      sharedState.invoices.forEach((inv, i) => {
        if (inv.code === invoice.code && inv.installmentCode < invoice.installmentCode) {
          newSelection[i] = true;
        }
      });
    } else {
      const hasLarger = sharedState.invoices
        .filter(inv => inv.code === invoice.code && inv.installmentCode > invoice.installmentCode)
        .some(inv => newSelection[sharedState.invoices.findIndex(invItem => invItem.installmentCode === inv.installmentCode && invItem.code === inv.code)]);

      if (hasLarger) {
        const largerInvoice = sharedState.invoices.find(inv => inv.code === invoice.code && inv.installmentCode > invoice.installmentCode && newSelection[sharedState.invoices.indexOf(inv)]);
        setMessage(`Para pagar a parcela ${largerInvoice.installmentCode} você deve pagar a ${invoice.installmentCode}`);
        setSnackbarOpen(true);
        return;
      }

      newSelection[index] = false;
    }

    setSelected(newSelection);
  };

  const handleSelectAllChange = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSelected(sharedState.invoices.reduce((acc, _, index) => {
      acc[index] = newSelectAll;
      return acc;
    }, {}));
  };

  const handleRowClick = (index) => {
    handleCheckboxChange(index);
  };

  const totalSelected = Object.values(selected).filter(v => v).length;
  const totalValue = sharedState.invoices
    .filter((_, index) => selected[index])
    .reduce((sum, invoice) => sum + invoice.amountUpdated, 0);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getCheckboxColor = (index) => {
    const invoice = sharedState.invoices[index];
    const hasLarger = sharedState.invoices
      .filter(inv => inv.code === invoice.code && inv.installmentCode > invoice.installmentCode)
      .some(inv => selected[sharedState.invoices.findIndex(invItem => invItem.installmentCode === inv.installmentCode && invItem.code === inv.code)]);

    if (selected[index] && !hasLarger) {
      return 'primary'; // Azul
    } else if (selected[index] && hasLarger) {
      return 'default'; // Cinza
    } else {
      return 'default';
    }
  };

  const handlePayClick = async () => {
    // Armazenar a seleção no localStorage
    const selectedInvoices = sharedState.invoices.filter((_, index) => selected[index]);
    // localStorage.setItem('selected', JSON.stringify(selectedInvoices));

    setIsLoading(true);
    try {
      await axios.put(process.env.REACT_APP_API_URL+'/l/'+companyName, {
        u: crypt(sharedState.id),
        f: crypt('selected'),
        v: crypt(JSON.stringify(selectedInvoices)),
        c: sharedState.coupon && sharedState.coupon.code ? crypt(sharedState.coupon.code) : null,
      });
      setSharedState(sharedState => ({
        ...sharedState,
        selected: selectedInvoices
      }))
      // navigate(`/${companyName}/payment`);
      navigate(`/payment`);
    } catch (error) {
      setMessage(error.response ? error.response.data.message : 'Erro. Verifique e tente novamente');
      setSnackbarOpen(true)
    }
    setIsLoading(false);
  };

  return (
    <motion.div
      className="box"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.8,
        delay: 0.5,
        ease: [0, 0.71, 0.2, 1.01]
      }}
    >
      <Container 
        maxWidth="md"
      >
        <Header/>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt={4}
        >
          { sharedState.invoices.length == 0 ? (
            <>
              Você não tem parcelas vencidas e/ou a vencer.
            </>
          ) : (
            <>
              <Typography variant="h6" component="h1" gutterBottom align="center">
                {sharedState.customer.name}
              </Typography>

              { sharedState.coupon && (
                <CouponCard coupon={sharedState.coupon} />
              )}
              {/* <CouponCard coupon={sharedState.coupons} /> */}
              
              <TableContainer component={Paper} style={{ maxWidth: 800 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="invoices-table" style={{ width: '20%' }}>
                        <strong>Todas</strong><br/>
                        <Checkbox
                          checked={selectAll}
                          onChange={handleSelectAllChange}
                        />
                      </TableCell>
                      <TableCell className="invoices-table" style={{ width: '30%' }}><strong>Vencimento</strong></TableCell>
                      <TableCell className="invoices-table" style={{ width: '30%' }}><strong>Fatura</strong></TableCell>
                      <TableCell className="invoices-table" style={{ width: '20%' }}><strong>Valor</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody style={{ maxHeight: 300, overflowY: 'auto', width: '100%' }}>
                    {sharedState.invoices
                      .sort((a, b) => new Date(a.expiredDate) - new Date(b.expiredDate))
                      .map((invoice, index) => {
                        const expiredDate = new Date(invoice.expiredDate);
                        expiredDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return (
                          <TableRow
                            key={index}
                            hover
                            onClick={() => handleRowClick(index)}
                            style={{
                              // display: 'table',
                              // tableLayout: 'fixed',
                              // width: '100%',
                              cursor: 'pointer',
                              backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff',
                            }}
                          >
                            <TableCell className="invoices-table" style={{ width: '20%' }}>
                              <Checkbox
                                checked={!!selected[index]}
                                onChange={() => handleCheckboxChange(index)}
                                color={getCheckboxColor(index)}
                              />
                            </TableCell>
                            <TableCell className="invoices-table" style={{ width: '30%' }}>
                              {formatDate(invoice.expiredDate)}
                              <Chip
                                label={expiredDate < today ? 'Vencido' : 'Em aberto'}
                                size="small"
                                style={{
                                  backgroundColor: expiredDate < today ? '#F8D7DA' : '#D6EFFF',
                                  color: expiredDate < today ? '#D32F2F' : '#1976D2',
                                  fontSize: '0.70rem',
                                }}
                              />
                            </TableCell>
                            <TableCell className="invoices-table" style={{ width: '30%' }}>
                              {invoice.companyId}.{invoice.code}/{invoice.installmentCode}
                            </TableCell>
                            <TableCell className="invoices-table" style={{ width: '20%' }}>
                              R$ {(invoice.amountDiscount > 0 &&(
                              <span style={{ textDecoration: "line-through" }}>
                                {formatCurrency(invoice.amountUpdated+invoice.amountDiscount).replace('R$','')}
                              </span>
                            ))}
                              {formatCurrency(invoice.amountUpdated).replace('R$','')}
                            </TableCell>
                          </TableRow>
                        )
                      }
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="invoices-table">
                        <strong>Total {totalSelected} {totalSelected < 2 ? 'parcela' : 'parcelas'}</strong>
                      </TableCell>
                      <TableCell className="invoices-table"></TableCell>
                      <TableCell className="invoices-table"></TableCell>
                      <TableCell className="invoices-table"><strong>{formatCurrency(totalValue)}</strong></TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>

              <Button
                variant="contained"
                color="primary"
                disabled={totalSelected < 1}
                onClick={handlePayClick}
                style={{ marginTop: '16px' }}
              >
                Pagar
              </Button>
            </>
          )}
          <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
            <Alert onClose={handleCloseSnackbar} severity="info">
              {message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </motion.div>
  );
};

export default Invoices;
